import React, {useEffect, useRef, useState, FormEvent} from 'react';
import {Row, Col, Dropdown, Menu, message as antdMessage} from 'antd';
import {UserOutlined, MenuOutlined} from '@ant-design/icons';
import './style.css';
import {getCookie} from 'typescript-cookie';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import GeneralModal, {GeneralModalRef} from '../Modals/general/component';
import {RootState} from '../../../store/rootReducer';
import * as authActions from '../../../store/modules/auth/actions';
import ButtonComponent from '../../atoms/Buttons';
import AuthDialog from '../../organisms/Forms/auth/component';
import SelectRoleDialog from '../../organisms/Forms/SelectRole/component';
import {roleTypes} from '../../../utils/types';
import productService from '../../../services/productService';
import {handleLogout, useIsMobile} from '../../../utils/helpers';
import {trackEvent} from '../../../analytics/posthog';

type HeaderProps = {
    onOpenSidebar?: () => void;
};

const Header: React.FC<HeaderProps> = ({onOpenSidebar}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const roles = (currentUser?.roles || []) as roleTypes[];
    const generalModalRef = useRef<GeneralModalRef>(null);

    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [dialogType, setDialogType] = useState<'login' | 'registration' | 'reset'>('login');
    const [lockedSignupRole, setLockedSignupRole] = useState<string | undefined>(undefined);
    const [showSelectRoleDialog, setShowSelectRoleDialog] = useState(false);

    const token = getCookie('token');
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!token) dispatch(authActions.logoutSuccess());
    }, [token, dispatch]);

    useEffect(() => {
        const handleOpen = (event: Event) => {
            const roleType = (event as CustomEvent<{ roleType?: string }>).detail?.roleType;
            const signupSource = (event as CustomEvent<{ signupSource?: string }>).detail?.signupSource;
            setLockedSignupRole(roleType);
            setDialogType('registration');
            setShowAuthDialog(true);
            trackEvent('signup_started', {
                signup_source: signupSource || 'unknown',
                selected_role: roleType
            });
        };
        window.addEventListener('open-signup', handleOpen);
        return () => window.removeEventListener('open-signup', handleOpen);
    }, []);

    const handleManageBilling = async (): Promise<void> => {
        try {
            const response = await productService.createPortalSession({});
            if (response?.data.url) window.location.href = response.data.url;
        } catch (err: any) {
            antdMessage.error(err.message || 'Error creating portal session.');
        }
    };

    const handleMenuClick = ({key}: { key: string }) => {
        switch (key) {
            case 'profile':
                navigate('/profile');
                break;
            case 'billing':
                handleManageBilling();
                break;
            case 'switch-account':
                setShowSelectRoleDialog(true);
                break;
            case 'logout':
                handleLogout(dispatch, navigate)();
                break;
            default:
                break;
        }
    };

    const menu = (
        <Menu onClick={handleMenuClick} style={{fontSize: 16}}>
            <Menu.Item key="profile">My profile</Menu.Item>
            <Menu.Item key="billing">Manage billing</Menu.Item>
            {roles.length > 1 && <Menu.Item key="switch-account">Switch account</Menu.Item>}
            <Menu.Item key="logout">Log out</Menu.Item>
        </Menu>
    );

    return (
        <>
            <header className="main" style={{backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                <Row justify="space-between" align="middle" wrap className="headerInner">
                    <Col flex="none" style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        {isMobile && isAuthenticated && (
                            <MenuOutlined style={{fontSize: 22, cursor: 'pointer', color: '#1677ff'}}
                                          onClick={() => onOpenSidebar?.()}/>
                        )}
                        <img src="/assets/images/promotify-transparent.png" alt="Logo" className="logo"
                             onClick={() => navigate('/home')}/>
                    </Col>

                    <Col flex="auto" className="rightCol">
                        {isAuthenticated ? (
                            <Dropdown overlay={menu} trigger={['click']}>
                                <UserOutlined className="userIcon"/>
                            </Dropdown>
                        ) : (
                            <div className="authButtons">
                                <ButtonComponent
                                    buttonType="primary"
                                    label="Sign up"
                                    onClick={async (e: FormEvent) => {
                                        e.preventDefault();
                                        trackEvent('landing_header_signup_clicked', {
                                            signup_source: 'landing_header_signup'
                                        });
                                        setLockedSignupRole(undefined);
                                        setDialogType('registration');
                                        setShowAuthDialog(true);
                                        trackEvent('signup_started', {
                                            signup_source: 'landing_header_signup'
                                        });
                                    }}
                                />
                                <ButtonComponent
                                    buttonType="primary"
                                    label="Log in"
                                    onClick={async (e: FormEvent) => {
                                        e.preventDefault();
                                        trackEvent('landing_header_login_clicked');
                                        setDialogType('login');
                                        setShowAuthDialog(true);
                                    }}
                                />
                            </div>
                        )}
                    </Col>
                </Row>

                {showAuthDialog && (
                    <AuthDialog type={dialogType} onClose={() => setShowAuthDialog(false)}
                                lockedRoleType={dialogType === 'registration' ? lockedSignupRole : undefined}
                                onOpenDialog={mode => {
                                    setLockedSignupRole(undefined);
                                    setDialogType(mode as 'login' | 'registration' | 'reset');
                                    setShowAuthDialog(true);
                                }}
                    />
                )}

                <GeneralModal ref={generalModalRef}/>
                {showSelectRoleDialog && (
                    <SelectRoleDialog visible onClose={() => setShowSelectRoleDialog(false)}/>
                )}
            </header>
        </>
    );
};

export default Header;
